/** @jsxImportSource preact */
// (Named here too, so the composer also renders in bun test, which reads the root tsconfig.)
import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { ComposerProps } from "../../sdk/theme";
import type { ComposerApi, ComposerProtector, ContentOrigin, ProtectedBody } from "../../sdk/web";
import type { Address, AttachmentInfo, Identity, MessageDetail } from "../../shared/types";
import { archiveAnswered, laterOptions, pickDateTime, REMIND_OPTIONS } from "../actions";
import { api as apiClient, isOffline, signImageUrls } from "../api";
import { joinPlain, splitPlain } from "../assist-text";
import {
  cleanHtml,
  draftRemote,
  markFromSource,
  ownSignature,
  QUOTED_MARK,
  restoreRemote,
  SIGNATURE_CLASS,
  showParkedImages,
  unproxyImages,
} from "../clean-html";
import { escapeHtml, formatFullDate, formatSize, htmlToText, textToHtml } from "../format";
import { useComposerLargeFile, useComposerTools } from "../modules";
import { queueDraft, queueSend, sendOrQueue } from "../offline";
import { overridable } from "../overrides";
import {
  app,
  type ComposerWindow,
  closeComposer,
  errorToast,
  mailChanged,
  toast,
  useStore,
} from "../store";
import { Editor } from "./Editor";
import { Icon } from "./Icon";
import { formatChip, RecipientInput } from "./RecipientInput";
import { IconButton, Menu, Spinner } from "./ui";

type Prefill = {
  identityId: string | null;
  to: Address[];
  cc: Address[];
  bcc: Address[];
  subject: string;
  quoteHtml: string;
  sourceId: string | null;
  mode: ComposerWindow["mode"];
  /** The server's word that the source is in Spam, handed back with the draft. */
  junkProof?: string;
};

/** What a new message starts from when the server can't be reached. */
const OFFLINE_PREFILL: Prefill = {
  identityId: null,
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  quoteHtml: "",
  sourceId: null,
  mode: "new",
};

const SEND_LIMIT = 5 * 1024 * 1024;
/** The email being answered or forwarded, as the composer quotes it. */
const QUOTE_PARTS =
  '.cm-quote-intro, .cm-quote-intro + blockquote, blockquote[type="cite"], .cm-forward';
/** Inline images and download links, which stay where they are when writing help rewrites. */
const KEPT_PARTS = ".shared-file, img";
/** Formatting that becomes plain text when writing help rewrites the text. */
const FORMATTING =
  "b, strong, i, em, u, s, strike, font, ul, ol, li, h1, h2, h3, h4, h5, h6, pre, code, table, blockquote, [style]";

const isQuote = (node: Node | null) => node instanceof Element && node.matches(QUOTE_PARTS);
const isKept = (node: Node) =>
  node instanceof Element && (node.matches(KEPT_PARTS) || node.querySelector(KEPT_PARTS) !== null);
const hasText = (nodes: readonly Node[]) => nodes.some((node) => node.textContent?.trim());

/** A link's address when its words say something else, so the new text has to show it. */
const shownAddress = (link: Element) => {
  const href = (link.getAttribute("href") ?? "").replace(/^mailto:/i, "");
  const label = link.textContent?.trim() ?? "";
  return /^(https?:\/\/|[^:/\s]+@)/i.test(href) && label && label !== href ? href : null;
};

/** Text for the model, with each link's address after its words so the new text keeps it. */
const textOf = (nodes: readonly Node[]) => {
  const holder = document.createElement("div");
  holder.append(...nodes.map((node) => node.cloneNode(true)));
  for (const link of holder.querySelectorAll("a[href]")) {
    const href = shownAddress(link);
    if (href) link.textContent = `${link.textContent?.trim()} (${href})`;
  }
  return htmlToText(holder.innerHTML);
};

/** Whether rewriting turns formatting into plain text, or shows link addresses. */
const reformats = (nodes: readonly Node[]) =>
  nodes.some(
    (node) =>
      node instanceof Element &&
      (node.matches(FORMATTING) ||
        node.querySelector(FORMATTING) !== null ||
        [node, ...node.querySelectorAll("a[href]")].some(
          (link) => link.matches("a[href]") && shownAddress(link) !== null,
        )),
  );

/** The person's part of the message, and how to put new text in its place. */
type OwnPart = { text: string; formatted: boolean; replace: (text: string) => void };

/**
 * The person's own words in the rich editor: above the signature and outside the quoted email,
 * where they answer (above the quote, or below it). Text between quoted parts, inline images
 * and download links stay as they are. A message instead when that can't be done.
 */
const ownRich = (
  root: HTMLElement,
  done: (html: string) => void,
  /** With nothing written yet, answer below the quote (the person's setting for replies). */
  answerBelow: boolean,
): OwnPart | string => {
  const children = [...root.childNodes];
  const signature = children.findIndex(
    (node) => node instanceof Element && node.classList.contains(SIGNATURE_CLASS),
  );
  if (signature === -1 && !children.some(isQuote)) {
    // No marks, as after switching from plain text: tell the parts apart the way plain text does.
    const parts = splitPlain(textOf(children), answerBelow);
    if (parts.before.length > 0 || parts.after.length > 0) {
      // Rebuilding from text would lose them.
      if (root.querySelector(KEPT_PARTS))
        return "Writing help can't rewrite this email without losing its images or download links.";
      return {
        text: parts.own.join("\n"),
        formatted: reformats(children),
        replace: (text: string) => {
          root.innerHTML = textToHtml(joinPlain(parts, text));
          done(root.innerHTML);
        },
      };
    }
  }
  const end = signature === -1 ? children.length : signature;
  // What the person may have written in a stretch: not images or links, nor the blank line
  // that keeps the quote apart.
  const writable = (from: number, to: number) =>
    children
      .slice(from, to)
      .filter((node) => !isKept(node) && !(node.nodeName === "BR" && isQuote(node.nextSibling)));
  const quotes = children.slice(0, end).map(isQuote);
  const firstQuote = quotes.indexOf(true);
  const lastQuote = quotes.lastIndexOf(true);
  let start = 0;
  let stop = end;
  if (firstQuote !== -1) {
    const below = writable(lastQuote + 1, end);
    // Answering below the quote: written there already, or nothing above it and room below
    // (the signature under the quote leaves room too).
    if (
      hasText(below) ||
      (!hasText(writable(0, firstQuote)) && (below.length > 0 || signature !== -1 || answerBelow))
    )
      start = lastQuote + 1;
    else stop = firstQuote;
  }
  const nodes = writable(start, stop);
  // An image between the lines would end up below the new text, apart from its line.
  const written = nodes.filter((node) => node.textContent?.trim());
  const from = children.indexOf(written[0] as ChildNode);
  const to = children.indexOf(written[written.length - 1] as ChildNode);
  if (children.slice(from, to).some(isKept))
    return "Writing help can't rewrite text with an image or download link in the middle. Move it below your text, then try again.";
  const anchor =
    nodes[0] ?? children.slice(start, stop).find((node) => !isKept(node)) ?? children[stop] ?? null;
  return {
    text: textOf(nodes),
    formatted: reformats(nodes),
    replace: (text: string) => {
      const template = document.createElement("template");
      template.innerHTML = text
        .split("\n")
        .map((line) => (line.trim() ? `<div>${textToHtml(line)}</div>` : "<div><br></div>"))
        .join("");
      root.insertBefore(template.content, anchor);
      for (const node of nodes) node.remove();
      done(root.innerHTML);
    },
  };
};

const signatureBlock = (html: string) =>
  html.trim()
    ? `<div class="${SIGNATURE_CLASS}"><br>-- <br>${cleanHtml(html)}</div>`
    : `<div class="${SIGNATURE_CLASS}"></div>`;

type Draft = {
  id: string | null;
  identityId: string;
  to: Address[];
  cc: Address[];
  bcc: Address[];
  subject: string;
  html: string;
  text: string;
  mode: ComposerWindow["mode"];
  sourceId: string | null;
  /** The message the composer opened on, sent with each save (Edit as new has no sourceId). */
  originId: string | null;
  /** Prefill's proof that the source was in Spam when the composer opened. */
  junkProof: string | null;
  /** Someone else's content in it came from Spam: its remote images never load on their own. */
  fromJunk: boolean;
  /**
   * A tool put in content from Spam (a template saved from it), which no proof covers: the
   * server takes the composer's word for it, as it only adds caution.
   */
  junkContent?: boolean;
  /**
   * Where replies go instead of the address's own Reply-To, set on the draft through the API:
   * shown, so it never goes out unseen. null once the person took it off.
   */
  replyTo?: string | null;
};

// Stacking order for floating composers: the one touched last sits on top.
let topLayer = 61;

const DESKTOP = "(min-width: 861px)";

const DefaultComposer = ({ window: win, stack = 0 }: ComposerProps) => {
  const me = useStore(app, (state) => state.me);
  // Every call from this window goes to the mailbox it was opened in, even after switching.
  const api = useMemo(() => apiClient.in(win.mailbox), [win.mailbox]);
  const sharedBox = useStore(app, (state) =>
    win.mailbox ? state.mailboxes.find((entry) => entry.id === win.mailbox) : undefined,
  );
  const [identities, setIdentities] = useState<Identity[]>(
    win.mailbox ? [] : (me?.identities ?? []),
  );
  const [draft, setDraft] = useState<Draft | null>(null);
  const [initialHtml, setInitialHtml] = useState("");
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
  const [uploading, setUploading] = useState(0);
  // Panels modules show above the buttons, such as writing help.
  const [panels, setPanels] = useState<Record<string, ComponentChildren>>({});
  // What modules add: toolbar tools, and a way to send files too big to attach.
  const tools = useComposerTools();
  const [showTools, setShowTools] = useState(false);
  const largeFile = useComposerLargeFile();
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [richText, setRichText] = useState(me?.settings.composeHtml ?? true);
  const [maximized, setMaximized] = useState(false);
  const [saving, setSaving] = useState<"idle" | "saving" | "saved">("idle");
  const [hidden, setHidden] = useState(false);
  const [dragging, setDragging] = useState(false);
  // Where the window was dragged to; null keeps it centred on the page.
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [layer, setLayer] = useState(() => ++topLayer);
  const moveFrom = useRef<{ dx: number; dy: number; moved: boolean } | null>(null);
  // Modules that keep the message's text in the browser (encryption), and that make its body.
  const [privateFor, setPrivateFor] = useState<ReadonlySet<string>>(new Set());
  const protectors = useRef(new Map<string, ComposerProtector>());
  /** "Remind me if no reply in N days", set from the send menu. */
  const [tracking, setTracking] = useState(me?.settings.emailTracking ?? false);
  const [remindDays, setRemindDays] = useState<number | null>(null);
  const dirty = useRef(false);
  const latest = useRef<Draft | null>(null);
  const savePromise = useRef<Promise<string> | null>(null);
  /** Set by Discard: work still under way (an upload) stops, and says nothing of failing. */
  const discarded = useRef(false);
  const editor = useRef<HTMLDivElement | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const inlineInput = useRef<HTMLInputElement>(null);
  latest.current = draft;

  // Mail from others goes into this page, so it's cleaned, and its remote images wait
  // for the send unless they may always load. Spam never loads them on its own, nor does mail
  // that quotes Spam (the Sent copy of a reply to it): prefill's proof says so (fromJunk).
  const mayLoad = (me?.settings.loadRemoteContent ?? "always") === "always";
  const remote = mayLoad && !win.junk && !draft?.fromJunk;

  // Build the starting draft: prefill from the server, plus signature and quote.
  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        // Includes masked addresses, so a reply to one goes out from it. Offline, your own
        // addresses will do for writing a message to send later.
        const identities = await api
          .get<Identity[]>("/compose/identities")
          .catch((error: unknown) => {
            if (isOffline(error) && !win.mailbox && me) return me.identities;
            throw error;
          });
        if (cancelled) return;
        setIdentities(identities);
        if (win.mode === "edit" && win.sourceId) {
          const source = await api.get<MessageDetail>(`/messages/${win.sourceId}`);
          if (source.isDraft) {
            if (cancelled) return;
            // Your own draft: its images show through the image proxy the server points them
            // at, and go back to their own URLs when it's saved or sent (payload).
            // The quoted mail in a saved reply or forward is someone else's: its images wait,
            // as they did when the reply was first written. An "Edit as new" copy waits whole.
            const parking = draftRemote(source.draft?.mode, remote, source.draft?.fromJunk);
            let body = source.html;
            // A reply or forward saved before quoted content was marked: whatever loads a URL
            // the answered mail also loads was quoted, even if editing moved it out of the quote.
            if (
              body !== null &&
              parking === "quotes" &&
              !body.includes(QUOTED_MARK) &&
              source.draft?.sourceId
            ) {
              const original = await api
                .get<MessageDetail>(`/messages/${encodeURIComponent(source.draft.sourceId)}`)
                .catch(() => null);
              if (cancelled) return;
              if (original?.html) body = markFromSource(body, original.html);
            }
            const html = body === null ? textToHtml(source.text) : cleanHtml(body, parking);
            setDraft({
              id: source.id,
              identityId: source.identityId ?? identities[0]?.id ?? "",
              to: source.to,
              cc: source.cc,
              bcc: source.bcc,
              subject: source.subject,
              html,
              text: source.text,
              mode: source.draft?.mode ?? "new",
              sourceId: source.draft?.sourceId ?? null,
              originId: null,
              junkProof: null,
              fromJunk: source.draft?.fromJunk ?? false,
              ...(source.draft?.replyTo ? { replyTo: source.draft.replyTo } : {}),
            });
            setInitialHtml(html);
            setAttachments(source.attachments);
            setShowCc(source.cc.length > 0);
            setShowBcc(source.bcc.length > 0);
            return;
          }
        }
        const params = new URLSearchParams({ mode: win.mode === "edit" ? "edit" : win.mode });
        if (win.sourceId) params.set("source", win.sourceId);
        const prefill = await api
          .get<Prefill>(`/compose/prefill?${params}`)
          .catch((error: unknown) => {
            if (isOffline(error) && win.mode === "new") return OFFLINE_PREFILL;
            throw error;
          });
        if (cancelled) return;
        const identity =
          identities.find((entry) => entry.id === prefill.identityId) ?? identities[0];
        const signature = signatureBlock(identity?.signatureHtml ?? "");
        const quoteAtTop = (me?.settings.replyQuotePosition ?? "top") === "top";
        const fromJunk = Boolean(win.junk || prefill.junkProof);
        const quoteHtml = cleanHtml(prefill.quoteHtml, mayLoad && !fromJunk);
        let html: string;
        if (win.mode === "edit") html = quoteHtml;
        else if (win.mode === "new")
          html = `<div>${win.body ? textToHtml(win.body) : "<br>"}</div>${signature}`;
        else if (win.mode === "forward") html = `<div><br></div>${signature}<br>${quoteHtml}`;
        else
          html = quoteAtTop
            ? `<div><br></div>${signature}<br>${quoteHtml}`
            : `${quoteHtml}<div><br></div>${signature}`;
        const to = [...prefill.to, ...(win.to ?? []).map((address) => ({ name: "", address }))];
        setDraft({
          id: null,
          identityId: identity?.id ?? "",
          to,
          cc: prefill.cc,
          bcc: prefill.bcc,
          subject: win.subject ?? prefill.subject,
          html,
          text: "",
          // "edit" marks a copy of someone's mail, so a reopen keeps its images parked.
          mode: win.mode,
          sourceId: win.mode === "edit" ? null : prefill.sourceId,
          // The server decides whether the draft answers Spam from these, never from our word.
          originId: prefill.sourceId,
          junkProof: prefill.junkProof ?? null,
          fromJunk,
        });
        setInitialHtml(html);
        setShowCc(prefill.cc.length > 0);
        // Forwards bring the original attachments along: create the draft right away.
        if (win.mode === "forward" && win.sourceId) dirty.current = true;
      } catch (error) {
        errorToast(error);
        closeComposer(win.key);
      }
    };
    void start();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (patch: Partial<Draft>) => {
    dirty.current = true;
    setSaving("idle");
    setDraft((current) => (current ? { ...current, ...patch } : current));
  };

  const payload = (value: Draft) => {
    // The quoted mail's marks stay in the saved draft (the server drops them when it sends).
    const html = richText ? unproxyImages(restoreRemote(value.html, true)) : null;
    return {
      ...(value.id ? { id: value.id } : {}),
      identityId: value.identityId,
      to: value.to.map(formatChip),
      cc: value.cc.map(formatChip),
      bcc: value.bcc.map(formatChip),
      subject: value.subject,
      html,
      text: richText ? htmlToText(value.html) : value.text,
      mode: value.mode,
      sourceId: value.sourceId,
      ...(value.originId && value.originId !== value.sourceId ? { originId: value.originId } : {}),
      ...(value.junkProof ? { junkProof: value.junkProof } : {}),
      ...(value.junkContent ? { junkContent: true } : {}),
      // The server keeps a Reply-To the draft has unless told it was taken off.
      ...(value.replyTo === null ? { replyTo: null } : {}),
    };
  };

  /**
   * Saves now (one save at a time) and returns the draft id. `sending`: the save right before
   * sending now, which a mailbox over its storage limit may still make.
   */
  const save = async (sending = false): Promise<string> => {
    if (savePromise.current) await savePromise.current.catch(() => {});
    const value = latest.current;
    if (!value) throw new Error("The draft is still loading.");
    if (value.id && !dirty.current) return value.id;
    dirty.current = false;
    setSaving("saving");
    const run = api
      .post<{ id: string; attachments: AttachmentInfo[] }>("/drafts", {
        ...payload(value),
        ...(sending ? { sending: true } : {}),
      })
      .then((result) => {
        setDraft((current) => (current ? { ...current, id: result.id } : current));
        if (latest.current) latest.current = { ...latest.current, id: result.id };
        if (!value.id) setAttachments(result.attachments);
        setSaving("saved");
        return result.id;
      })
      .catch((error: unknown) => {
        dirty.current = true;
        setSaving("idle");
        throw error;
      });
    savePromise.current = run;
    return run;
  };

  // Autosave a couple of seconds after the last change.
  useEffect(() => {
    if (!draft || !dirty.current) return;
    const timer = setTimeout(() => void save().catch(() => {}), 2500);
    return () => clearTimeout(timer);
  }, [draft]);

  const switchIdentity = (identityId: string) => {
    const identity = identities.find((entry) => entry.id === identityId);
    if (richText && editor.current) {
      const block = ownSignature(editor.current);
      if (block) block.outerHTML = signatureBlock(identity?.signatureHtml ?? "");
      update({ identityId, html: editor.current.innerHTML });
    } else update({ identityId });
  };

  const upload = async (file: File, inline: boolean, throwOnFailure = false) => {
    const attached = attachments.reduce((sum, attachment) => sum + attachment.size, 0);
    // Base64 makes attachments about a third bigger on the wire.
    if (!inline && (file.size + attached) * 1.37 > SEND_LIMIT) {
      // A module (Large files) can send it another way, such as a download link.
      if (largeFile) {
        await largeFile(file, composerApi).catch(errorToast);
        return;
      }
      toast(`${file.name} would take the message over the 5 MB sending limit.`, {
        tone: "error",
      });
      return;
    }
    if (file.size > SEND_LIMIT) {
      toast(`${file.name} is larger than the 5 MB sending limit.`, { tone: "error" });
      return;
    }
    setUploading((count) => count + 1);
    try {
      const id = await save();
      if (discarded.current) return;
      const form = new FormData();
      form.set("file", file);
      if (inline) form.set("inline", "1");
      const attachment = await api.upload<AttachmentInfo>(`/drafts/${id}/attachments`, form);
      setAttachments((list) => [...list, attachment]);
      if (inline && editor.current) {
        editor.current.focus();
        document.execCommand(
          "insertHTML",
          false,
          `<img src="/api/attachments/${attachment.id}" alt="${escapeHtml(file.name)}" style="max-width:100%">`,
        );
        update({ html: editor.current.innerHTML });
      }
    } catch (error) {
      if (throwOnFailure) throw error;
      if (!discarded.current) errorToast(error);
    } finally {
      setUploading((count) => count - 1);
    }
  };

  /**
   * What content a tool puts in does with remote images: as a reopened draft of the kind it
   * came from (a template saved from Edit as new is a copy of someone's mail, one saved from
   * Spam never loads them), and never more than this composer's own mail may.
   */
  const insertedParking = (origin?: ContentOrigin) =>
    draftRemote(origin?.copy ? "edit" : undefined, remote, origin?.junk);

  const showInserted = (root: HTMLElement) => {
    void showParkedImages(root, signImageUrls).then((shown) => {
      if (shown && editor.current === root) update({ html: root.innerHTML });
    });
  };

  /** What the composer's toolbar tools from modules (like templates) work with. */
  const composerApi: ComposerApi = {
    attachFile: (file) => upload(file, false, true),
    ownText: () => ownPart(),
    details: () => {
      const identity = identities.find((entry) => entry.id === draft?.identityId);
      return {
        from: identity
          ? identity.name
            ? `${identity.name} <${identity.address}>`
            : identity.address
          : "",
        fromAddress: identity?.address ?? "",
        to: draft ? [...draft.to, ...draft.cc].map(formatChip) : [],
        recipients: draft
          ? [...draft.to, ...draft.cc, ...draft.bcc].map((address) => address.address)
          : [],
        bcc: draft ? draft.bcc.map((address) => address.address) : [],
        mode: draft?.mode ?? "new",
        sourceId: draft?.sourceId ?? null,
      };
    },
    setSubject: (subject) => update({ subject }),
    private: privateFor.size > 0,
    setPrivate: (id, on) =>
      setPrivateFor((current) => {
        if (current.has(id) === on) return current;
        const next = new Set(current);
        if (on) next.add(id);
        else next.delete(id);
        return next;
      }),
    setProtector: (id, protector) => {
      if (protector) protectors.current.set(id, protector);
      else protectors.current.delete(id);
    },
    setPanel: (id, panel) =>
      setPanels((current) => {
        const { [id]: _, ...rest } = current;
        return panel === null ? rest : { ...rest, [id]: panel };
      }),
    mailbox: win.mailbox,
    api,
    subject: draft?.subject ?? "",
    richText,
    insert: (content) => {
      if (richText && editor.current) {
        const signature = ownSignature(editor.current);
        // A fresh message has only an empty line before the signature; the content replaces it.
        const first = editor.current.firstElementChild;
        if (first && first !== signature && first.textContent === "" && !first.querySelector("img"))
          first.remove();
        const html = cleanHtml(content.html, insertedParking(content.origin));
        if (signature) signature.insertAdjacentHTML("beforebegin", html);
        else editor.current.insertAdjacentHTML("afterbegin", html);
        showInserted(editor.current);
        update({
          html: editor.current.innerHTML,
          ...(draft?.subject || !content.subject ? {} : { subject: content.subject }),
          // Content from Spam makes the whole draft Spam's: kept parked on a reopen, held back
          // in the reader and in its Sent copy, and in whatever answers or copies that.
          ...(content.origin?.junk ? { fromJunk: true, junkContent: true } : {}),
        });
      } else {
        setDraft((current) =>
          current
            ? {
                ...current,
                text: `${htmlToText(content.html).trim()}\n\n${current.text.trimStart()}`,
                subject: current.subject || (content.subject ?? ""),
              }
            : current,
        );
        dirty.current = true;
      }
    },
    append: (content) => {
      if (richText && editor.current) {
        const signature = ownSignature(editor.current);
        const html = cleanHtml(content.html, insertedParking());
        if (signature) signature.insertAdjacentHTML("beforebegin", html);
        else editor.current.insertAdjacentHTML("beforeend", html);
        showInserted(editor.current);
        update({ html: editor.current.innerHTML });
      } else {
        setDraft((current) =>
          current
            ? { ...current, text: `${current.text.trimEnd()}\n\n${content.text}\n` }
            : current,
        );
        dirty.current = true;
      }
    },
    busy: async (work) => {
      setUploading((count) => count + 1);
      try {
        return await work;
      } finally {
        setUploading((count) => count - 1);
      }
    },
    scoringContent: () => {
      const html = richText
        ? unproxyImages(restoreRemote(editor.current?.innerHTML ?? draft?.html ?? "", false))
        : "";
      return {
        subject: draft?.subject ?? "",
        html,
        text: richText ? htmlToText(html) : (draft?.text ?? ""),
        attachmentNames: attachments.map((attachment) => attachment.filename),
      };
    },
    content: (options) => {
      const subject = draft?.subject ?? "";
      const origin = {
        ...(draft?.mode === "edit" ? { copy: true } : {}),
        ...(draft?.fromJunk || win.junk ? { junk: true } : {}),
      };
      if (!richText) return { subject, html: textToHtml(draft?.text ?? ""), origin };
      // The signature is added per address, so it stays out.
      const copy = document.createElement("div");
      copy.innerHTML = editor.current?.innerHTML ?? draft?.html ?? "";
      ownSignature(copy)?.remove();
      const html = unproxyImages(restoreRemote(copy.innerHTML, options?.keepMarks));
      return { subject, html, origin };
    },
  };

  /** What the person wrote so far: not the signature, nor the email being answered. */
  const ownPart = (): OwnPart | string => {
    const answerBelow =
      (draft?.mode === "reply" || draft?.mode === "reply-all") &&
      me?.settings.replyQuotePosition === "bottom";
    if (draft && !richText) {
      const parts = splitPlain(draft.text, answerBelow);
      return {
        text: parts.own.join("\n"),
        formatted: false,
        replace: (text: string) => update({ text: joinPlain(parts, text) }),
      };
    }
    if (!editor.current) return "The editor isn't ready yet. Try again in a moment.";
    return ownRich(editor.current, (html) => update({ html }), answerBelow);
  };

  const removeAttachment = async (attachment: AttachmentInfo) => {
    try {
      await api.del(`/attachments/${attachment.id}`);
      setAttachments((list) => list.filter((entry) => entry.id !== attachment.id));
    } catch (error) {
      errorToast(error);
    }
  };

  const validate = (): boolean => {
    if (!draft) return false;
    if (draft.to.length + draft.cc.length + draft.bcc.length === 0) {
      toast("Add at least one recipient.", { tone: "error" });
      return false;
    }
    if (uploading > 0) {
      toast("Wait for the attachments to finish uploading.", { tone: "error" });
      return false;
    }
    if (!draft.subject.trim() && !window.confirm("Send without a subject?")) return false;
    const body = richText ? htmlToText(draft.html) : draft.text;
    if (
      /\b(attach(ed|ment)|enclosed|bijlage|anbei|ci-joint|adjunto)\b/i.test(
        `${draft.subject} ${body.split(/\n>/)[0]}`,
      ) &&
      attachments.filter((a) => !a.inline).length === 0
    ) {
      if (!window.confirm("You mention an attachment but nothing is attached. Send anyway?"))
        return false;
    }
    return true;
  };

  /** The body a module makes from the saved draft (signed or encrypted); null for the usual one. */
  const protect = async (id: string): Promise<ProtectedBody | null | false> => {
    for (const protector of protectors.current.values()) {
      const result = await protector.protect(id);
      if (result !== null) return result;
    }
    return null;
  };

  /** Moves the replied-to conversation out of its folder into Archive, with an Undo. */
  const archiveSource = (sourceId: string) =>
    // Where the message was when the composer opened on it, if this is that message.
    archiveAnswered(
      win.mailbox,
      sourceId,
      win.sourceId === sourceId ? (win.sourceFolderId ?? null) : null,
    );

  /** Offline: the message waits in the outbox and goes out once the connection is back. */
  const sendLater = async (sendAt: number | null, archiveAfter = false, tried = false) => {
    const value = latest.current;
    if (!value) return;
    try {
      const needsConnection = [...protectors.current.values()][0];
      if (needsConnection) throw new Error(needsConnection.offline);
      await queueSend(win.mailbox, payload(value), {
        sendAt,
        remindDays,
        tracking,
        archive: archiveAfter ? value.sourceId : null,
        tried,
      });
    } catch (error) {
      setHidden(false);
      errorToast(error);
      return;
    }
    toast(
      `You're offline. The message will be ${sendAt === null ? "sent" : "scheduled"} once you're back online.`,
    );
    closeComposer(win.key);
  };

  const send = async (sendAt: number | null = null, archiveAfter = false) => {
    if (!validate()) return;
    let id: string;
    let protectedBody: ProtectedBody | null;
    try {
      id = await save(sendAt === null);
      const result = await protect(id);
      if (result === false) return;
      protectedBody = result;
    } catch (error) {
      if (isOffline(error)) void sendLater(sendAt, archiveAfter);
      else errorToast(error);
      return;
    }
    if (sendAt !== null) {
      try {
        await api.post(`/drafts/${id}/send`, { sendAt, pgp: protectedBody, remindDays, tracking });
        toast(`Scheduled for ${formatFullDate(sendAt)}`);
        mailChanged();
        closeComposer(win.key);
      } catch (error) {
        if (isOffline(error)) void sendLater(sendAt, false, true);
        else errorToast(error);
      }
      return;
    }
    const delay = (me?.settings.undoSendSeconds ?? 5) * 1000;
    setHidden(true);
    let undone = false;
    const deliver = async () => {
      if (undone) return;
      try {
        await api.post(`/drafts/${id}/send`, {
          sendAt: null,
          pgp: protectedBody,
          remindDays,
          tracking,
        });
      } catch (error) {
        if (isOffline(error)) {
          // The send may have reached the server with only its answer lost.
          void sendLater(null, archiveAfter, true);
          return;
        }
        setHidden(false);
        errorToast(error);
        return;
      }
      mailChanged();
      closeComposer(win.key);
      const sourceId = latest.current?.sourceId;
      if (archiveAfter && sourceId) await archiveSource(sourceId).catch(errorToast);
      else toast("Message sent");
    };
    if (delay === 0) {
      await deliver();
      return;
    }
    const timer = setTimeout(deliver, delay);
    toast("Sending…", {
      ms: delay,
      action: {
        label: "Undo",
        run: () => {
          undone = true;
          clearTimeout(timer);
          setHidden(false);
        },
      },
    });
  };

  const discard = async () => {
    discarded.current = true;
    closeComposer(win.key);
    // A save on its way (the first one creates the draft, and a forward's copies of the
    // attachments) is waited for, so the draft it makes goes too, and so is one queued behind it.
    for (let pending = savePromise.current; pending; ) {
      await pending.catch(() => {});
      pending = savePromise.current === pending ? null : savePromise.current;
    }
    const id = latest.current?.id;
    if (id) {
      await sendOrQueue(
        "POST",
        "/messages/delete",
        { ids: [id], permanent: true },
        win.mailbox,
      ).catch(errorToast);
      mailChanged();
    }
    toast("Draft discarded");
  };

  const close = async () => {
    if (dirty.current) {
      try {
        await save();
      } catch (error) {
        const value = latest.current;
        if (!isOffline(error) || !value) {
          errorToast(error);
          return;
        }
        // Offline: the draft is saved once the connection is back.
        try {
          await queueDraft(win.mailbox, payload(value));
        } catch (failure) {
          errorToast(failure);
          return;
        }
        toast("You're offline. The draft will be saved once you're back online.");
      }
    }
    if (latest.current?.id) mailChanged();
    closeComposer(win.key);
  };

  const toggleRichText = () => {
    if (!draft) return;
    if (richText) {
      update({ text: htmlToText(draft.html) });
    } else {
      const html = textToHtml(draft.text);
      setInitialHtml(html);
      update({ html });
    }
    setRichText(!richText);
  };

  if (hidden) return null;
  const minimized = win.minimized;
  const total = attachments.reduce((sum, attachment) => sum + attachment.size, 0);
  // Replies can archive the conversation they answer once they go out.
  const canArchive =
    (draft?.mode === "reply" || draft?.mode === "reply-all") && Boolean(draft.sourceId);
  const floating = !minimized && !maximized;
  const toggleMinimized = () =>
    app.set((state) => ({
      composers: state.composers.map((entry) =>
        entry.key === win.key ? { ...entry, minimized: !entry.minimized } : entry,
      ),
    }));
  const title =
    draft?.subject ||
    (win.mode === "new" ? "New message" : win.mode === "forward" ? "Forward" : "Reply");

  return (
    <div
      data-part="composer"
      class={`composer${floating ? " floating" : ""}${minimized ? " minimized" : ""}${maximized ? " maximized" : ""}${dragging ? " dragging" : ""}`}
      style={{
        zIndex: layer,
        "--stack": `${stack * 28}px`,
        ...(position && { "--x": `${position.x}px`, "--y": `${position.y}px` }),
      }}
      onPointerDownCapture={() => {
        if (layer !== topLayer) setLayer(++topLayer);
      }}
      role="dialog"
      aria-label={title}
      onDragOver={(event) => {
        if (event.dataTransfer?.types.includes("Files")) {
          event.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(event) => {
        if (event.currentTarget === event.target) setDragging(false);
      }}
      onDrop={(event) => {
        if (!event.dataTransfer?.files.length) return;
        event.preventDefault();
        setDragging(false);
        for (const file of event.dataTransfer.files) void upload(file, false);
      }}
      onKeyDown={(event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
          event.preventDefault();
          void send();
        }
      }}
    >
      <header
        class="composer-head"
        onPointerDown={(event) => {
          // Drag the floating window by its title bar, on a desktop-sized screen.
          if (!floating || event.button !== 0 || !matchMedia(DESKTOP).matches) return;
          if ((event.target as Element).closest("button")) return;
          const box = (event.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
          moveFrom.current = {
            dx: event.clientX - box.left,
            dy: event.clientY - box.top,
            moved: false,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const from = moveFrom.current;
          if (!from) return;
          const box = (event.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
          const x = event.clientX - from.dx;
          const y = event.clientY - from.dy;
          if (!from.moved && Math.hypot(x - box.left, y - box.top) < 4) return;
          from.moved = true;
          // Keep the whole window on screen so its buttons stay in reach.
          setPosition({
            x: Math.min(Math.max(x, 8), Math.max(8, innerWidth - box.width - 8)),
            y: Math.min(Math.max(y, 8), Math.max(8, innerHeight - box.height - 8)),
          });
        }}
        onPointerUp={() => {
          if (moveFrom.current && !moveFrom.current.moved) moveFrom.current = null;
        }}
        onClick={() => {
          const dragged = moveFrom.current?.moved;
          moveFrom.current = null;
          if (!dragged) toggleMinimized();
        }}
      >
        <span class="composer-title">
          {title}
          {sharedBox && <span class="composer-mailbox"> · {sharedBox.address}</span>}
        </span>
        <span class="composer-status">
          {saving === "saving" ? "Saving…" : saving === "saved" ? "Saved" : ""}
        </span>
        <span class="composer-head-actions" onClick={(event) => event.stopPropagation()}>
          <IconButton
            icon="minus"
            label={minimized ? "Restore" : "Minimize"}
            size={15}
            onClick={toggleMinimized}
          />
          <IconButton
            icon="maximize"
            label={maximized ? "Exit full screen" : "Full screen"}
            size={15}
            onClick={() => setMaximized(!maximized)}
          />
          <IconButton icon="x" label="Save and close" size={15} onClick={() => void close()} />
        </span>
      </header>
      {/* Kept while minimized (hidden by the stylesheet), so the editor and the tools' choices,
          such as encrypting, stay as they were. */}
      {!draft ? (
        !minimized && <Spinner />
      ) : (
        <div class="composer-body">
          <div class="composer-fields">
            <label class="from-field">
              <span class="recipient-label">From</span>
              <select
                value={draft.identityId}
                onChange={(event) => switchIdentity((event.target as HTMLSelectElement).value)}
              >
                {identities
                  .filter((identity) => !identity.masked || identity.id === draft.identityId)
                  .map((identity) => (
                    <option value={identity.id} key={identity.id}>
                      {identity.name ? `${identity.name} <${identity.address}>` : identity.address}
                    </option>
                  ))}
              </select>
            </label>
            {draft.replyTo && (
              <div class="from-field reply-to-field">
                <span class="recipient-label">Reply-To</span>
                <span class="reply-to-address" title="Replies to this message go here">
                  {draft.replyTo}
                </span>
                <button
                  type="button"
                  class="link small"
                  aria-label={`Remove Reply-To ${draft.replyTo}`}
                  onClick={() => update({ replyTo: null })}
                >
                  Remove
                </button>
              </div>
            )}
            <div class="recipient-row">
              <RecipientInput
                label="To"
                value={draft.to}
                autofocus={draft.to.length === 0}
                onChange={(to) => update({ to })}
              />
              <span class="cc-toggles">
                {!showCc && (
                  <button type="button" class="link small" onClick={() => setShowCc(true)}>
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button type="button" class="link small" onClick={() => setShowBcc(true)}>
                    Bcc
                  </button>
                )}
              </span>
            </div>
            {showCc && (
              <RecipientInput label="Cc" value={draft.cc} onChange={(cc) => update({ cc })} />
            )}
            {showBcc && (
              <RecipientInput label="Bcc" value={draft.bcc} onChange={(bcc) => update({ bcc })} />
            )}
            <input
              class="subject-input"
              placeholder="Subject"
              aria-label="Subject"
              value={draft.subject}
              onInput={(event) => update({ subject: (event.target as HTMLInputElement).value })}
            />
          </div>
          {richText ? (
            <Editor
              key={initialHtml}
              initialHtml={initialHtml}
              editorRef={(element) => {
                editor.current = element;
                if (element && draft.to.length > 0) {
                  element.focus();
                  const range = document.createRange();
                  range.setStart(element, 0);
                  range.collapse(true);
                  window.getSelection()?.removeAllRanges();
                  window.getSelection()?.addRange(range);
                }
              }}
              onChange={(html) => update({ html })}
              onPasteImage={(file) => void upload(file, true)}
              onInsertImage={() => inlineInput.current?.click()}
            />
          ) : (
            <textarea
              class="plain-editor"
              aria-label="Message"
              value={draft.text}
              onInput={(event) => update({ text: (event.target as HTMLTextAreaElement).value })}
            />
          )}
          {attachments.filter((attachment) => !attachment.inline).length > 0 && (
            <div class="composer-attachments">
              {attachments
                .filter((attachment) => !attachment.inline)
                .map((attachment) => (
                  <span class="attachment-pill" key={attachment.id}>
                    <Icon name="paperclip" size={13} />
                    <span class="attachment-name">{attachment.filename}</span>
                    <span class="muted small">{formatSize(attachment.size)}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${attachment.filename}`}
                      onClick={() => void removeAttachment(attachment)}
                    >
                      ×
                    </button>
                  </span>
                ))}
            </div>
          )}
          {total > SEND_LIMIT * 0.7 && (
            <p class={`size-meter${total > SEND_LIMIT ? " over" : ""}`}>
              Attachments: {formatSize(total)} of the 5 MB sending limit. Bigger files are added as
              download links.
            </p>
          )}
          {Object.entries(panels).map(([id, panel]) => (
            <div key={id} class="composer-panel">
              {panel}
            </div>
          ))}
          {uploading > 0 && (
            <p class="muted small uploading">
              Uploading {uploading} file{uploading === 1 ? "" : "s"}…
            </p>
          )}
          <div class="composer-measurement">
            <label class="checkbox">
              <input
                type="checkbox"
                checked={tracking}
                disabled={privateFor.size > 0 || !me?.modules?.includes("analytics")}
                onChange={(event) => setTracking(event.currentTarget.checked)}
              />{" "}
              Measure opens & clicks
            </label>
            <span class="muted small">Reported opens are estimates.</span>
          </div>
          <div
            class="composer-tools"
            id={`writing-tools-${win.key}`}
            hidden={!showTools}
            role="toolbar"
            aria-label="Writing tools"
          >
            {tools.map((Tool, index) => (
              <Tool key={index} composer={composerApi} />
            ))}
            <button
              type="button"
              class="ghost small"
              onClick={toggleRichText}
              title="Switch between rich text and plain text"
            >
              {richText ? "Plain text" : "Rich text"}
            </button>
          </div>
          <footer class="composer-foot">
            <div class="send-group">
              <button type="button" class="primary" onClick={() => void send()}>
                Send
              </button>
              <Menu
                trigger={({ toggle }) => (
                  <button
                    type="button"
                    class="primary send-more"
                    aria-label="More send options"
                    title="More send options"
                    onClick={toggle}
                  >
                    <Icon name="chevronDown" size={14} />
                  </button>
                )}
                items={() => [
                  ...(canArchive
                    ? [
                        {
                          label: "Send and archive",
                          icon: "archive" as const,
                          onSelect: () => void send(null, true),
                        },
                        { separator: true } as const,
                      ]
                    : []),
                  { heading: "Schedule send" },
                  ...laterOptions().map((option) => ({
                    label: option.label,
                    icon: "clock" as const,
                    onSelect: () => void send(option.at),
                  })),
                  {
                    label: "Pick date and time…",
                    icon: "calendar",
                    onSelect: () => {
                      const at = pickDateTime("Send at");
                      if (at !== null) void send(at);
                    },
                  },
                  { separator: true },
                  { heading: "Remind me if no reply" },
                  ...REMIND_OPTIONS.map((option) => ({
                    label: option.label,
                    icon: "alarm" as const,
                    checked: remindDays === option.days,
                    onSelect: () => setRemindDays(remindDays === option.days ? null : option.days),
                  })),
                ]}
              />
            </div>
            {remindDays !== null && (
              <button
                type="button"
                class="remind-chip"
                title="Comes back to your Inbox if nobody replies. Click to remove."
                onClick={() => setRemindDays(null)}
              >
                <Icon name="alarm" size={12} />
                {remindDays === 7 ? "1 week" : `${remindDays} day${remindDays === 1 ? "" : "s"}`}
                <span aria-hidden="true">×</span>
              </button>
            )}
            <IconButton
              icon="paperclip"
              label="Attach files"
              onClick={() => fileInput.current?.click()}
            />
            <button
              type="button"
              class="icon-button"
              aria-label="Writing tools and more options"
              aria-expanded={showTools}
              aria-controls={`writing-tools-${win.key}`}
              onClick={() => setShowTools(!showTools)}
            >
              <Icon name="more" size={24} />
            </button>
            <span class="spacer" />
            <IconButton icon="trash" label="Discard draft" onClick={() => void discard()} />
          </footer>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={(event) => {
              const input = event.target as HTMLInputElement;
              for (const file of input.files ?? []) void upload(file, false);
              input.value = "";
            }}
          />
          <input
            ref={inlineInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const input = event.target as HTMLInputElement;
              const file = input.files?.[0];
              if (file) void upload(file, true);
              input.value = "";
            }}
          />
        </div>
      )}
      {dragging && <div class="drop-hint">Drop files to attach</div>}
    </div>
  );
};

export const Composer = overridable("Composer", DefaultComposer);

export const Composers = () => {
  const composers = useStore(app, (state) => state.composers);
  return (
    <div class="composer-dock" data-part="composer-dock">
      {composers.map((entry) => (
        <Composer
          key={entry.key}
          window={entry}
          stack={composers.filter((other) => !other.minimized).indexOf(entry)}
        />
      ))}
    </div>
  );
};
